import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiSave, FiX, FiUser, FiShield } from 'react-icons/fi';
import api from '../services/api';
import Modal from '../components/Modal';
import '../components/Button.css';
import './Gebruikers.css';

const Gebruikers = () => {
  const [gebruikers, setGebruikers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    gebruikersnaam: '',
    email: '',
    wachtwoord: '',
    rol: 'gebruiker',
    actief: true
  });

  useEffect(() => {
    fetchGebruikers();
  }, []);

  const fetchGebruikers = async () => {
    try {
      const response = await api.get('/auth/users');
      console.log('[Gebruikers] Received users:', response.data);
      console.log('[Gebruikers] Number of users:', response.data?.length);
      setGebruikers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      console.error('Error response:', error.response?.data);
      alert('Fout bij ophalen gebruikers: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({
      gebruikersnaam: '',
      email: '',
      wachtwoord: '',
      rol: 'gebruiker',
      actief: true
    });
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      gebruikersnaam: user.gebruikersnaam,
      email: user.email || '',
      wachtwoord: '', // Don't pre-fill password
      rol: user.rol,
      actief: user.actief === 1
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze gebruiker wilt verwijderen?')) {
      return;
    }

    try {
      await api.delete(`/auth/users/${id}`);
      fetchGebruikers();
      alert('Gebruiker verwijderd');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Fout bij verwijderen: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.gebruikersnaam) {
      alert('Gebruikersnaam is verplicht');
      return;
    }

    if (!editingUser && !formData.wachtwoord) {
      alert('Wachtwoord is verplicht voor nieuwe gebruikers');
      return;
    }

    if (formData.wachtwoord && formData.wachtwoord.length < 6) {
      alert('Wachtwoord moet minimaal 6 tekens lang zijn');
      return;
    }

    try {
      if (editingUser) {
        // Update
        await api.put(`/auth/users/${editingUser.id}`, formData);
        alert('Gebruiker bijgewerkt');
      } else {
        // Create
        await api.post('/auth/register', formData);
        alert('Gebruiker aangemaakt');
      }
      setShowModal(false);
      fetchGebruikers();
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Fout: ' + (error.response?.data?.error || error.message));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('nl-NL');
  };

  if (loading) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="gebruikers">
      <div className="page-header">
        <h1 className="page-title">
          <FiUser /> Gebruikersbeheer
        </h1>
        <button className="btn btn-primary" onClick={handleCreate}>
          <FiPlus /> Nieuwe Gebruiker
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Gebruikersnaam</th>
              <th>Email</th>
              <th>Rol</th>
              <th>Status</th>
              <th>Laatste Login</th>
              <th>Aangemaakt</th>
              <th>Acties</th>
            </tr>
          </thead>
          <tbody>
            {gebruikers.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-state">
                  Geen gebruikers gevonden. Maak de eerste gebruiker aan.
                  {console.log('[Gebruikers] Render: geen gebruikers in array')}
                </td>
              </tr>
            ) : (
              gebruikers.map((user) => {
                console.log('[Gebruikers] Rendering user:', user);
                return (
                <tr key={user.id}>
                  <td><strong>{user.gebruikersnaam}</strong></td>
                  <td>{user.email || '-'}</td>
                  <td>
                    <span className={`role-badge ${user.rol === 'admin' ? 'admin' : 'user'}`}>
                      {user.rol === 'admin' && <FiShield />}
                      {user.rol === 'admin' ? 'Admin' : 'Gebruiker'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.actief === 1 ? 'active' : 'inactive'}`}>
                      {user.actief === 1 ? 'Actief' : 'Inactief'}
                    </span>
                  </td>
                  <td>{formatDate(user.last_login)}</td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon"
                        onClick={() => handleEdit(user)}
                        title="Bewerken"
                      >
                        <FiEdit />
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        onClick={() => handleDelete(user.id)}
                        title="Verwijderen"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              );
              })
            )}
          </tbody>
        </table>
      </div>

      <Modal show={showModal} onClose={() => setShowModal(false)}>
        <div className="modal-header">
          <h2>{editingUser ? 'Gebruiker Bewerken' : 'Nieuwe Gebruiker'}</h2>
          <button className="modal-close" onClick={() => setShowModal(false)}>
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="user-form">
          <div className="form-group">
            <label>Gebruikersnaam *</label>
            <input
              type="text"
              value={formData.gebruikersnaam}
              onChange={(e) => setFormData({ ...formData, gebruikersnaam: e.target.value })}
              required
              disabled={!!editingUser}
            />
          </div>

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label>
              Wachtwoord {editingUser ? '(laat leeg om niet te wijzigen)' : '*'}
            </label>
            <input
              type="password"
              value={formData.wachtwoord}
              onChange={(e) => setFormData({ ...formData, wachtwoord: e.target.value })}
              required={!editingUser}
              minLength={6}
            />
          </div>

          <div className="form-group">
            <label>Rol</label>
            <select
              value={formData.rol}
              onChange={(e) => setFormData({ ...formData, rol: e.target.value })}
            >
              <option value="gebruiker">Gebruiker</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.actief}
                onChange={(e) => setFormData({ ...formData, actief: e.target.checked })}
              />
              Actief
            </label>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
              Annuleren
            </button>
            <button type="submit" className="btn btn-primary">
              <FiSave /> {editingUser ? 'Bijwerken' : 'Aanmaken'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Gebruikers;
